import React, { useEffect, useState } from "react";
import axios from "axios";
import { Download, FileText, Play } from "lucide-react";
import { useUploadStore } from "../stores/videoUploadStore";

const VideoListPlayer = ({ surveyId }) => {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [videoError, setVideoError] = useState(null);
  const [downloadProgress, setDownloadProgress] = useState(null);
  const videosPerPage = 5;

  const { fetchVideoData, data } = useUploadStore();

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        setLoading(true);
        setError(null);
        await fetchVideoData(surveyId);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching videos:", err);
        // setError("Failed to load videos. Please try again later.");
        setLoading(false);
      }
    };

    if (surveyId) {
      fetchVideos();
    }
  }, [surveyId, fetchVideoData]);

  const videos = Array.isArray(data) ? data : [];

  const getVideoUrl = (processedPath) => {
    if (!processedPath) return null;
    if (processedPath.startsWith("http")) return processedPath;
    if (processedPath.startsWith("/api/media")) {
      return `${window.location.origin}${processedPath}`;
    }
    const baseUrl = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem("token"); // Get token from localStorage
    const url = `${baseUrl}api/videos/player/${processedPath}?token=${encodeURIComponent(token)}`;
    console.log("Generated video URL:", url); // Debug log
    return url;
  };

  const getFileName = (processedPath) => {
    if (!processedPath) return "Unknown";
    const parts = processedPath.split(/[\\/]/);
    return parts[parts.length - 1];
  };

  const downloadReport = async (videoId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}api/videos/${videoId}/report`,
        { responseType: "blob" }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `report-${videoId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading report:", error);
      alert("Error downloading report. Please try again later.");
    }
  };

  const downloadVideo = async (videoPath) => {
    if (!videoPath) {
      alert("Video path is missing");
      return;
    }

    try {
      setDownloadProgress("Preparing download...");
      const videoUrl = getVideoUrl(videoPath);
      const fileName = getFileName(videoPath);

      // Verify the video exists first
      const headResponse = await fetch(videoUrl, { method: "HEAD" });
      if (!headResponse.ok) {
        throw new Error(`Server returned ${headResponse.status}`);
      }

      // Create download link
      const link = document.createElement("a");
      link.href = videoUrl;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);

      setDownloadProgress("Download started...");
      link.click();

      // Clean up
      setTimeout(() => {
        document.body.removeChild(link);
        setDownloadProgress(null);
      }, 100);
    } catch (error) {
      console.error("Error downloading video:", error);
      setDownloadProgress(null);
      alert(
        `Error downloading video: ${error.message || "File not available"}`
      );
    }
  };

  const handleVideoError = (e, videoId) => {
    try {
      if (!e.target || !e.target.error) {
        throw new Error("Video element or error object is null");
      }

      const errorCode = e.target.error?.code || "Unknown";
      const errorMessage =
        e.target.error?.message || "No detailed error provided";

      console.error("Video playback error:", {
        errorCode,
        errorMessage,
        event: e,
      });

      setVideoError(
        `Failed to play video. Try downloading it instead. (Error: ${errorCode})`
      );
    } catch (err) {
      console.error("Error in handleVideoError:", err.message || err);
      setVideoError("Failed to play video due to an unexpected error.");
    }
  };

  // Pagination logic
  const indexOfLastVideo = currentPage * videosPerPage;
  const indexOfFirstVideo = indexOfLastVideo - videosPerPage;
  const currentVideos = videos.slice(indexOfFirstVideo, indexOfLastVideo);
  const totalPages = Math.ceil(videos.length / videosPerPage);

  return (
    <div className="container mx-auto px-4 py-6">
      {error && <div className="text-center text-red-600 mb-6">{error}</div>}
      {downloadProgress && (
        <div className="fixed top-4 right-4 bg-blue-500 text-white px-4 py-2 rounded shadow-lg">
          {downloadProgress}
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="text-center py-8">
            <p className="text-gray-500">Loading videos...</p>
          </div>
        ) : videos.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">No processed videos available.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Label
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Video
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentVideos.map((video) => (
                    <tr key={video._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {video.videoLabel || "No Label"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-blue-600">
                          {video.processedPath ? (
                            <span
                              className="hover:underline cursor-pointer"
                              onClick={() => {
                                setSelectedVideo(video);
                                setVideoError(null);
                              }}
                            >
                              {getFileName(video.processedPath) ||
                                video.filename ||
                                "No Video"}
                            </span>
                          ) : (
                            <span className="text-gray-500">
                              No video available
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex space-x-2">
                          {video.processedPath && (
                            <>
                              <button
                                className="p-2 rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100"
                                onClick={() => {
                                  setSelectedVideo(video);
                                  setVideoError(null);
                                }}
                                title="Play Video"
                              >
                                <Play size={18} />
                              </button>
                              <button
                                className="p-2 rounded-md bg-green-50 text-green-600 hover:bg-green-100"
                                onClick={() =>
                                  downloadVideo(video.processedPath)
                                }
                                title="Download Video"
                              >
                                <Download size={18} />
                              </button>
                            </>
                          )}
                          <button
                            className="p-2 rounded-md bg-purple-50 text-purple-600 hover:bg-purple-100"
                            onClick={() => downloadReport(video._id)}
                            title="Download Report"
                          >
                            <FileText size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
              <div className="flex-1 flex justify-between">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-center">
                  <div>
                    <p className="text-sm text-gray-700">
                      Page <span className="font-medium">{currentPage}</span> of{" "}
                      <span className="font-medium">{totalPages}</span>
                    </p>
                  </div>
                </div>
                <button
                  onClick={() =>
                    setCurrentPage(Math.min(totalPages, currentPage + 1))
                  }
                  disabled={currentPage === totalPages || totalPages === 0}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Video Player Modal */}
      {selectedVideo && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-screen overflow-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-2xl font-semibold text-gray-800">
                  {selectedVideo.videoLabel ||
                    getFileName(selectedVideo.processedPath)}
                </h2>
                <button
                  onClick={() => {
                    setSelectedVideo(null);
                    setVideoError(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {videoError && (
                <div className="mb-4 p-4 bg-red-50 text-red-600 rounded-md">
                  {videoError}
                  <button
                    onClick={() => downloadVideo(selectedVideo.processedPath)}
                    className="ml-2 text-red-700 underline"
                  >
                    Download instead
                  </button>
                </div>
              )}

              {selectedVideo.processedPath && (
                <div className="relative pt-[56.25%] bg-black rounded-lg overflow-hidden">
                  <video
                    key={selectedVideo._id}
                    controls
                    autoPlay
                    className="absolute top-0 left-0 w-full h-full"
                    onError={(e) => handleVideoError(e, selectedVideo._id)}
                  >
                    <source
                      src={getVideoUrl(selectedVideo.processedPath)}
                      type="video/mp4"
                    />
                    Your browser doesn't support HTML5 video.
                  </video>
                </div>
              )}

              <div className="mt-4 flex justify-between items-center">
                <button
                  onClick={() => downloadVideo(selectedVideo.processedPath)}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
                >
                  <Download className="mr-2" size={16} />
                  Download Video
                </button>
                <button
                  onClick={() => {
                    setSelectedVideo(null);
                    setVideoError(null);
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoListPlayer;